import type { XimiHttpResponse } from './ximiHttp';

const HTTP_UNAUTHORIZED = 401;

/**
 * App-side code (not an Ozmo code): Ozmo still answers 401 with a fresh SSO token, i.e. the
 * account is unknown or its Ximi access was disabled (PBI 106089). Ozmo infrastructure failures
 * never produce a 401 (they surface as 5xx or network errors).
 */
export const XIMI_ACCESS_REFUSED = 'XIMI_ACCESS_REFUSED';

/**
 * A 401 may only mean the SSO token expired between its read and the request: force one SSO
 * renew and replay once. A null renew (explicit SSO refusal) skips the replay; an unreachable
 * AuthProvider rejects with `XimiSsoUnavailableError` (transient, session kept by callers).
 */
export async function retryXimiCallOnceAfterSsoRenew<T>(
    call: () => Promise<XimiHttpResponse<T>>,
    renewSsoAccessToken: () => Promise<string | null>
): Promise<XimiHttpResponse<T>> {
    const response = await call();
    if (response.status !== HTTP_UNAUTHORIZED) return response;
    const renewedToken = await renewSsoAccessToken();
    if (!renewedToken) return response;
    return call();
}

/**
 * Error code of a failed `api/stimulin/*` call: a 401 that survived the SSO renew attempt (replay
 * refused, or renew refused by the SSO) means access refused.
 */
export function resolveXimiStimulinErrorCode(status: number, code?: string): string | undefined {
    if (status === HTTP_UNAUTHORIZED) return XIMI_ACCESS_REFUSED;
    return code;
}
